import axios from "axios";

// Centralized API URL - change this one place to update all API calls
const API_BASE_URL = "";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Drop only the auth tokens (same keys as AuthContext). localStorage.clear()
// would also wipe cookie consent, analytics prefs and recent searches.
const clearAuthTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
};

// Only admin pages bounce to the admin login; a public page that gets a 401
// just sees the failed request.
const redirectToAdminLogin = () => {
  const { pathname } = window.location;
  if (pathname.startsWith("/admin") && pathname !== "/admin/login") {
    window.location.href = "/admin/login";
  }
};

// One refresh at a time: the server rotates the refresh token, so parallel
// 401s must share a single refresh call (a second call with the old refresh
// token would fail and log the admin out).
let refreshPromise = null;
const refreshAccessToken = (refreshToken) => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/api/auth/refresh-token`, { refreshToken })
      .then((response) => {
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Store the new access token; only replace the refresh token when the
        // server actually returned a new one — otherwise we'd overwrite a valid
        // refresh token with `undefined` (stored as the string "undefined"),
        // which silently breaks every subsequent refresh and logs the admin out.
        localStorage.setItem("accessToken", accessToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (error.response?.data?.code === "TOKEN_EXPIRED") {
        originalRequest._retry = true;

        // Another request already refreshed while this one was in flight:
        // just retry with the token that's stored now.
        const sentToken = String(originalRequest.headers?.Authorization || "").replace(/^Bearer /, "");
        const storedToken = localStorage.getItem("accessToken");
        if (!refreshPromise && storedToken && sentToken && storedToken !== sentToken) {
          originalRequest.headers.Authorization = `Bearer ${storedToken}`;
          return api(originalRequest);
        }

        try {
          const refreshToken = localStorage.getItem("refreshToken");

          if (!refreshToken) {
            // No refresh token, redirect to login
            clearAuthTokens();
            redirectToAdminLogin();
            return Promise.reject(error);
          }

          // Try to refresh the token (shared with any parallel 401s)
          const accessToken = await refreshAccessToken(refreshToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          clearAuthTokens();
          redirectToAdminLogin();
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Export API_BASE_URL for use in components
export { API_BASE_URL };

// Auth API
export const authAPI = {
  login: (email, password) => api.post("/api/auth/login", { email, password }),
  adminLogin: (email, password) => api.post("/api/auth/admin/login", { email, password }),
  register: (data) => api.post("/api/auth/register", data),
  refreshToken: (refreshToken) => api.post("/api/auth/refresh-token", { refreshToken }),
  logout: () => api.post("/api/auth/logout"),
  getMe: () => api.get("/api/auth/me"),
  forgotPassword: (email) => api.post("/api/auth/forgot-password", { email }),
  resetPassword: (token, password) =>
    api.post(`/api/auth/reset-password/${token}`, { password }),
  updateProfile: (formData) => api.put("/api/auth/profile", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getStats: () => api.get("/api/admin/stats"),
  getPending: () => api.get("/api/admin/pending"),

  // User Management
  createAdmin: (data) => api.post("/api/admin/users/create-admin", data),
  getAdmins: () => api.get("/api/admin/users/admins"),
  toggleUserStatus: (id) => api.patch(`/api/admin/users/${id}/toggle-status`),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),

  // Blog Approval
  approveBlog: (id) => api.patch(`/api/admin/blogs/${id}/approve`),
  rejectBlog: (id, reason) => api.patch(`/api/admin/blogs/${id}/reject`, { reason }),

  // Case Study Approval
  approveCaseStudy: (id) => api.patch(`/api/admin/case-studies/${id}/approve`),
  rejectCaseStudy: (id, reason) =>
    api.patch(`/api/admin/case-studies/${id}/reject`, { reason }),

  // Podcast Approval
  approvePodcast: (id) => api.patch(`/api/admin/podcasts/${id}/approve`),
  rejectPodcast: (id, reason) =>
    api.patch(`/api/admin/podcasts/${id}/reject`, { reason }),

  // News Approval
  approveNews: (id) => api.patch(`/api/admin/news/${id}/approve`),
  rejectNews: (id, reason) =>
    api.patch(`/api/admin/news/${id}/reject`, { reason }),

  // Product Approval
  approveProduct: (id) => api.patch(`/api/admin/products/${id}/approve`),
  rejectProduct: (id, reason) =>
    api.patch(`/api/admin/products/${id}/reject`, { reason }),

  // Client Approval
  approveClient: (id) => api.patch(`/api/admin/clients/${id}/approve`),
  rejectClient: (id, reason) =>
    api.patch(`/api/admin/clients/${id}/reject`, { reason }),

  // POC Approval
  approvePOC: (id) => api.patch(`/api/admin/pocs/${id}/approve`),
  rejectPOC: (id, reason) => api.patch(`/api/admin/pocs/${id}/reject`, { reason }),

  // Contacts
  getContacts: (params) => api.get("/api/admin/contacts", { params }),
  markContactRead: (id) => api.patch(`/api/admin/contacts/${id}/read`),
  addContactNote: (id, notes) => api.patch(`/api/admin/contacts/${id}/note`, { notes }),
  deleteContact: (id) => api.delete(`/api/admin/contacts/${id}`),
};

// Blog API
export const blogAPI = {
  // Public
  getAll: (params) => api.get("/api/blogs", { params }),
  getBySlug: (slug) => api.get(`/api/blogs/slug/${slug}`),
  // Admin editor load: the public /:id only serves approved posts.
  getById: (id) => api.get(`/api/blogs/admin/${id}`),
  getByCategory: (category) => api.get(`/api/blogs/category/${category}`),
  getByTag: (tag) => api.get(`/api/blogs/tag/${tag}`),
  getCategories: () => api.get("/api/blogs/meta/categories"),
  getRelated: (slug) => api.get(`/api/blogs/related/${slug}`),

  // Admin
  getAllAdmin: (params) => api.get("/api/blogs/admin/all", { params }),
  create: (data) => api.post("/api/blogs", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/blogs/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/blogs/${id}`),

  // Editor image upload
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append("image", file);
    return api.post("/api/blogs/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Comments
  getComments: (id) => api.get(`/api/blogs/${id}/comments`),
  addComment: (id, text) => api.post(`/api/blogs/${id}/comments`, { text }),
  deleteComment: (blogId, commentId) =>
    api.delete(`/api/blogs/comments/${blogId}/${commentId}`),
};

// Case Study API
export const caseStudyAPI = {
  // Public
  getAll: (params) => api.get("/api/case-studies", { params }),
  getBySlug: (slug) => api.get(`/api/case-studies/slug/${slug}`),
  getById: (id) => api.get(`/api/case-studies/admin/${id}`),
  getCategories: () => api.get("/api/case-studies/meta/categories"),

  // Admin
  getAllAdmin: (params) => api.get("/api/case-studies/admin/all", { params }),
  create: (data) => api.post("/api/case-studies", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/case-studies/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/case-studies/${id}`),
};

// Podcast API
export const podcastAPI = {
  // Public
  getAll: (params) => api.get("/api/podcasts", { params }),
  getBySlug: (slug) => api.get(`/api/podcasts/slug/${slug}`),
  getById: (id) => api.get(`/api/podcasts/admin/${id}`),
  getCategories: () => api.get("/api/podcasts/meta/categories"),

  // Admin
  getAllAdmin: (params) => api.get("/api/podcasts/admin/all", { params }),
  create: (data) => api.post("/api/podcasts", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/podcasts/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/podcasts/${id}`),
};

// POC API
// create/update must be handed a real FormData instance — the multipart header
// below only produces a valid body (with a boundary) if the payload is FormData.
export const pocAPI = {
  // Public — responses never include videoUrl; the server strips it.
  getAll: (params) => api.get("/api/pocs", { params }),
  getBySlug: (slug) => api.get(`/api/pocs/slug/${slug}`),
  getById: (id) => api.get(`/api/pocs/${id}`),
  getCategories: () => api.get("/api/pocs/meta/categories"),
  requestPOC: (id, data) => api.post(`/api/pocs/${id}/request`, data),

  // Admin
  getAllAdmin: (params) => api.get("/api/pocs/admin/all", { params }),
  create: (data) => api.post("/api/pocs", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/pocs/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/pocs/${id}`),

  // Leads
  getLeads: (params) => api.get("/api/pocs/admin/leads", { params }),
  updateLead: (id, data) => api.patch(`/api/pocs/admin/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/api/pocs/admin/leads/${id}`),
};

// News API
export const newsAPI = {
  // Public
  getAll: (params) => api.get("/api/news", { params }),
  getBySlug: (slug) => api.get(`/api/news/slug/${slug}`),
  getById: (id) => api.get(`/api/news/admin/${id}`),
  getByCategory: (category) => api.get(`/api/news/category/${category}`),
  getCategories: () => api.get("/api/news/meta/categories"),
  getFeatured: () => api.get("/api/news/meta/featured"),

  // Admin
  getAllAdmin: (params) => api.get("/api/news/admin/all", { params }),
  create: (data) => api.post("/api/news", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/news/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/news/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post("/api/contacts", data),
};

// Testimonial API
export const testimonialAPI = {
  // Public
  getAll: () => api.get("/api/testimonials"),
  getById: (id) => api.get(`/api/testimonials/${id}`),

  // Admin
  getAllAdmin: () => api.get("/api/testimonials/admin/all"),
  create: (data) => api.post("/api/testimonials", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/testimonials/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  toggle: (id) => api.patch(`/api/testimonials/${id}/toggle`),
  delete: (id) => api.delete(`/api/testimonials/${id}`),
};

// Appreciation API
export const appreciationAPI = {
  // Public
  getAll: () => api.get("/api/appreciations"),
  getById: (id) => api.get(`/api/appreciations/${id}`),

  // Admin
  create: (data) => api.post("/api/appreciations", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/appreciations/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/appreciations/${id}`),
};

// Product API ("Our In-House Products" cards + Products detail page)
export const productAPI = {
  // Public
  getAll: () => api.get("/api/products"),
  // Lean card fields only — used by the Home "In-House Products" row so the
  // home page doesn't download every product's full detail-page payload.
  getCards: () => api.get("/api/products/cards"),
  getBySlug: (slug) => api.get(`/api/products/slug/${slug}`),
  requestDemo: (id, data) => api.post(`/api/products/${id}/request-demo`, data),

  // Admin
  getAllAdmin: (params) => api.get("/api/products/admin/all", { params }),
  getByIdAdmin: (id) => api.get(`/api/products/admin/${id}`),
  create: (data) => api.post("/api/products", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/products/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/products/${id}`),
  // Superadmin: save the Home marquee ("homeOrder") or Products tab ("tabOrder")
  // order. `ids` must list every product exactly once.
  reorder: (field, ids) => api.patch("/api/products/admin/order", { field, ids }),

  // Superadmin approval
  approve: (id) => api.patch(`/api/admin/products/${id}/approve`),
  reject: (id, reason) => api.patch(`/api/admin/products/${id}/reject`, { reason }),
};

// Client API ("Empowering Success Stories" logo marquee)
export const clientAPI = {
  // Public
  getAll: () => api.get("/api/clients"),

  // Admin
  getAllAdmin: (params) => api.get("/api/clients/admin/all", { params }),
  create: (data) => api.post("/api/clients", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/clients/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/clients/${id}`),

  // Superadmin approval
  approve: (id) => api.patch(`/api/admin/clients/${id}/approve`),
  reject: (id, reason) => api.patch(`/api/admin/clients/${id}/reject`, { reason }),
};

// Policy API (Legal pages: Privacy Policy, Cookie Policy, Terms of Use, etc.)
export const policyAPI = {
  // Public
  getAll: () => api.get("/api/policies"),
  getBySlug: (slug) => api.get(`/api/policies/slug/${slug}`),

  // Admin
  getAllAdmin: () => api.get("/api/policies/admin/all"),
  getByIdAdmin: (id) => api.get(`/api/policies/admin/${id}`),
  create: (data) => api.post("/api/policies", data),
  update: (id, data) => api.put(`/api/policies/${id}`, data),
  delete: (id) => api.delete(`/api/policies/${id}`),
};

// Hero API (Home hero banner text + CTA)
export const heroAPI = {
  // Public
  get: () => api.get("/api/hero"),

  // Admin
  getAdmin: () => api.get("/api/hero/admin"),
  update: (data) => api.put("/api/hero", data),
};

// Stat API (Home "Our Track Record So Far" counter cards)
export const statAPI = {
  // Public
  getAll: () => api.get("/api/stats"),

  // Admin
  getAllAdmin: () => api.get("/api/stats/admin/all"),
  getByIdAdmin: (id) => api.get(`/api/stats/admin/${id}`),
  create: (data) => api.post("/api/stats", data),
  update: (id, data) => api.put(`/api/stats/${id}`, data),
  delete: (id) => api.delete(`/api/stats/${id}`),
};

// Success Story API (About → Success Stories: listing + /success-stories/:slug detail)
export const successStoryAPI = {
  // Public
  getAll: () => api.get("/api/success-stories"),
  getBySlug: (slug) => api.get(`/api/success-stories/slug/${slug}`),

  // Admin
  getAllAdmin: () => api.get("/api/success-stories/admin/all"),
  getByIdAdmin: (id) => api.get(`/api/success-stories/admin/${id}`),
  create: (data) => api.post("/api/success-stories", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  update: (id, data) => api.put(`/api/success-stories/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  delete: (id) => api.delete(`/api/success-stories/${id}`),
};

// Category API
export const categoryAPI = {
  // Public - get active categories
  getAll: (type) => api.get("/api/categories", { params: type ? { type } : {} }),
  getByType: (type) => api.get(`/api/categories/type/${type}`),

  // Admin
  getAllAdmin: () => api.get("/api/categories/admin/all"),
  create: (data) => api.post("/api/categories", data),
  update: (id, data) => api.put(`/api/categories/${id}`, data),
  delete: (id) => api.delete(`/api/categories/${id}`),
  toggle: (id) => api.patch(`/api/categories/${id}/toggle`),
  reorder: (categories) => api.patch("/api/categories/reorder", { categories }),
};

// Search API (site-wide fuzzy search across all content types + static pages)
export const searchAPI = {
  // `opts` may include { type, limit, signal }. `signal` is an AbortController
  // signal so the header search box can cancel stale in-flight requests as the
  // user keeps typing (latest-response-wins).
  search: (q, opts = {}) => {
    const { type, limit, signal } = opts;
    const params = { q };
    if (type) params.type = type;
    if (limit) params.limit = limit;
    return api.get("/api/search", { params, signal });
  },
};

export default api;
